import { faComment, faPhone, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext } from 'react';

import Debug from '@/components/Debug';
import { DISPLAYED_CHANNELS } from '@/constants/messageChannels';
import { usePersonMessages } from '@/hooks/usePersonMessages';
import PersonContext from '@/pages/Person/PersonContext';
import { Channel } from '@/types/channel';
import type { Message } from '@/types/message';
import { ShowMoreText, Spinner, Typography } from '@/ui';
import { DualTime } from '@/ui/DualTime';
import formatStaffName from '@/utils/formatStaffName';
import { messageTitle } from '@/utils/messageSummary';

const RECENT_COUNT = 3;

const channelIcon = (channel: string) => {
  if (channel === Channel.VOICE) return faPhone;
  if (channel === Channel.SMS || channel === Channel.CHAT) return faComment;

  return faUser;
};

const PersonRecentContact = () => {
  const { person } = useContext(PersonContext);
  // The same filtering the messaging tab applies by default, so the two views
  // agree on what counts as contact.
  const { data, isError, isPending } = usePersonMessages(person.id, undefined, undefined, {
    auto: 'false',
    channel__in: DISPLAYED_CHANNELS
  });

  // The viewset's queryset orders newest first, so the first page leads with the
  // most recent contact.
  const messages: Message[] = (data?.pages?.[0]?.results ?? []).slice(0, RECENT_COUNT);

  return (
    <div className="relative">
      {isPending ? (
        <div className="mt-2 flex justify-center py-4">
          <Spinner label="Loading recent contact" />
        </div>
      ) : isError ? (
        <Typography className="mt-2 text-uniform-red-600 dark:text-uniform-red-500">
          Unable to load recent contact.
        </Typography>
      ) : !messages.length ? (
        <Typography className="mt-2" muted>
          No recent contact.
        </Typography>
      ) : (
        <ul className="mt-2 divide-y divide-uniform-gray-200 dark:divide-uniform-gray-700">
          {messages.map((message) => {
            const actor = message.created_by && formatStaffName({ staff: message.created_by });

            return (
              <li className="flex gap-2 py-2 first:pt-0 last:pb-0" key={message.id}>
                <FontAwesomeIcon
                  className="mt-1 text-uniform-gray-500 dark:text-uniform-gray-400"
                  icon={channelIcon(message.channel)}
                />

                <div>
                  <Typography weight="bold">{messageTitle(message)}</Typography>

                  {!!message.preview && <ShowMoreText>{message.preview}</ShowMoreText>}

                  <Typography muted>
                    <DualTime
                      className="text-inherit dark:text-inherit"
                      dateTime={new Date(message.sent_at)}
                      showTime={false}
                    />
                    {actor ? ` · ${actor}` : ''}
                  </Typography>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Debug object={{ messages }} />
    </div>
  );
};

export default React.memo(PersonRecentContact);
